/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Image, Text, Icon, Pressable } from '@components/atoms'
import Popover from '@mui/material/Popover'

interface PropsType {
	className?: string
	picture: string
	name: string
	color: string
	icon?: JSX.Element
	extpandingDisable?: boolean
}

export const Avatar = (props: PropsType) => {
	const className: string = props.className ?? ''
	const userName = props.name
	const backgroundColor = props.color === '' ? '#bdbdbd' : props.color
	const extpandingDisable: boolean = props.extpandingDisable ?? false

	const [picture, setPicture] = React.useState<string>('')
	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

	const open = Boolean(anchorEl)
	const id = open ? 'avatar-popover' : undefined

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	React.useEffect(() => {
		if (props.picture !== '') {
			setPicture(props.picture)
		}
	}, [props.picture])

	const RenderAvatar = ({ style }: { style?: React.CSSProperties }) => {
		return (
			<View
				css={[
					{
						position: 'relative',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexShrink: 0,
						width: '41px',
						height: '41px',
						backgroundColor: backgroundColor,
						borderRadius: '50%',
						fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
						fontSize: '1.25rem',
						lineHeight: 1,
						overflow: 'hidden',
						userSelect: 'none',
						color: '#fff',
					},
					{ ...style },
				]}
			>
				{picture !== '' ? (
					<Image
						src={picture}
						css={{
							width: '100%',
							height: '100%',
							textAlign: 'center',
							objectFit: 'cover',
							color: 'transparent',
							textIndent: '10000px',
						}}
					/>
				) : userName.length > 0 ? (
					<Text category="h4">{userName.charAt(0)}</Text>
				) : props.icon !== undefined ? (
					props.icon
				) : (
					<Icon name="account" css={{ color: '#FFF' }} />
				)}
			</View>
		)
	}

	return (
		<View css={style}>
			{extpandingDisable ? (
				<RenderAvatar />
			) : (
				<>
					<Pressable
						className={`avatarButton ${className}`}
						css={[{ background: picture !== '' ? backgroundColor : '#bdbdbd' }]}
						aria-describedby={id}
						onClick={handleClick}
					>
						<RenderAvatar />
					</Pressable>

					<Popover
						id={id}
						open={open}
						anchorEl={anchorEl}
						onClose={handleClose}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'left',
						}}
						sx={{ borderRadius: '50%' }}
					>
						<RenderAvatar style={{ width: '100px', height: '100px', borderRadius: 0 }} />
					</Popover>
				</>
			)}
		</View>
	)
}

const style = css({
	position: 'relative',

	'.avatarButton': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '40px',
		height: '40px',
		borderRadius: '50%',
		overflow: 'hidden',
	},
})
